import { HStack, Spacer, Stack, Text } from "@chakra-ui/react";
import * as React from "react";
import { LuPencil, LuPlus, LuUsers } from "react-icons/lu";
import { useParams } from "react-router-dom";

import {
  deleteCustomerProfileSubset,
  getCustomerProfileSubsets,
} from "../../../api/company-knowledge";
import { Button } from "../../../components/button";
import { CustomerProfileSubsetDialog } from "../../../components/customer-profile-subset-dialog";
import DataTable, { DataTableColumn } from "../../../components/data-table";
import { DeleteButton } from "../../../components/delete-button";
import { EmptyState } from "../../../components/empty-state";
import IconCircle from "../../../components/icon-circle";
import PageBody from "../../../components/page-body";
import { useLoading, useToaster } from "../../../hooks";
import { CustomerProfileSubset } from "../../../types/company-knowledge";

export default function CustomerProfileSubsetsPage() {
  const { workspace: workspaceSlug } = useParams();
  const [subsets, setSubsets] = React.useState<CustomerProfileSubset[]>([]);
  const [isLoading, withLoading] = useLoading();
  const [hasLoadedOnce, setHasLoadedOnce] = React.useState(false);
  const [isDialogOpen, setIsDialogOpen] = React.useState(false);
  const [editingSubset, setEditingSubset] =
    React.useState<CustomerProfileSubset | null>(null);
  const withToaster = useToaster();

  const loadSubsets = React.useCallback(async () => {
    if (!workspaceSlug) return;
    await withLoading(async () => {
      await withToaster(async () => {
        setSubsets(await getCustomerProfileSubsets(workspaceSlug));
        setHasLoadedOnce(true);
      });
    });
  }, [withLoading, withToaster, workspaceSlug]);

  React.useEffect(() => {
    loadSubsets();
  }, [loadSubsets]);

  const handleDelete = React.useCallback(
    async (id: number) => {
      if (!workspaceSlug) return;
      await withLoading(async () => {
        await withToaster(async () => {
          await deleteCustomerProfileSubset(workspaceSlug, id);
          await loadSubsets();
        });
      });
    },
    [loadSubsets, withLoading, withToaster, workspaceSlug],
  );

  const columns: DataTableColumn<CustomerProfileSubset>[] = [
    {
      id: "name",
      name: "Name",
      icon: <LuUsers />,
      width: 240,
      cell: ({ value }) => <Text fontWeight="medium">{value.name}</Text>,
    },
    {
      id: "description",
      name: "Description",
      width: 320,
      cell: ({ value }) => (
        <Text color="fg.muted" truncate>
          {value.description || "—"}
        </Text>
      ),
    },
    {
      name: "",
      width: 100,
      cell: ({ value }) => (
        <HStack justify="flex-end">
          <Button
            size="sm"
            title="Edit subset"
            variant="ghost"
            onClick={() => {
              setEditingSubset(value);
              setIsDialogOpen(true);
            }}
          >
            <LuPencil />
          </Button>
          <DeleteButton
            label="Delete"
            message={`Delete "${value.name}"?`}
            onDelete={() => handleDelete(value.id)}
          />
        </HStack>
      ),
    },
  ];

  const isEmpty = subsets.length === 0 && hasLoadedOnce;

  return (
    <PageBody>
      <Stack flex="1" gap="5">
        <HStack>
          <Stack gap="0.5">
            <Text color="fg.ink" fontSize="md" fontWeight="medium">
              ICP Subsets
            </Text>
            <Text color="fg.muted" fontSize="sm">
              Segments of your ideal customer with their own specifics. Subsets
              inherit the overall ICP.
            </Text>
          </Stack>
          {!isEmpty && (
            <>
              <Spacer />
              <Button
                onClick={() => {
                  setEditingSubset(null);
                  setIsDialogOpen(true);
                }}
              >
                <LuPlus /> Create Subset
              </Button>
            </>
          )}
        </HStack>
        {isEmpty ? (
          <HStack flex="1" alignItems="center">
            <EmptyState
              icon={<IconCircle Icon={LuUsers} size="md" variant="flat" />}
              title="No ICP Subsets"
              description="Create a subset to describe a more specific segment of your ideal customer."
            >
              <Button
                onClick={() => {
                  setEditingSubset(null);
                  setIsDialogOpen(true);
                }}
              >
                <LuPlus /> Create Subset
              </Button>
            </EmptyState>
          </HStack>
        ) : (
          <DataTable
            data={subsets}
            columns={columns}
            skeleton={isLoading && hasLoadedOnce ? 3 : undefined}
          />
        )}
      </Stack>
      <CustomerProfileSubsetDialog
        isOpen={isDialogOpen}
        onOpenChange={({ open }) => {
          if (!open) setEditingSubset(null);
          setIsDialogOpen(open);
        }}
        onSuccess={loadSubsets}
        subset={editingSubset}
        workspaceSlug={workspaceSlug ?? ""}
      />
    </PageBody>
  );
}
